/**
 * Web Google Sign-In for RE-AUTHENTICATION on browsers (not for regular sign-in).
 *
 * Regular web sign-in still goes through Supabase's `/auth/v1/authorize` and a
 * full-page redirect (see authService.signInWithGoogle). That flow is unusable
 * for the account-deletion re-auth step because it replaces the current
 * session and navigates away from the delete page — the user would come back
 * to a fresh, cookie-authenticated session but with all of the delete-page
 * state gone.
 *
 * This module uses Google Identity Services (GIS) instead: a popup / picker
 * that returns a Google `id_token` in-page, without navigating away. The
 * id_token is then POSTed to /api/auth/account[/permanent] as the reauth
 * credential, exactly like the native mobile path does with the token it gets
 * from the OS sheet. Backend verification (services/auth/account-reauth.service.ts)
 * is provider-agnostic — signInWithIdToken checks the JWKS + audience + nonce
 * and the caller confirms `user.id` matches the current session's user.
 *
 * ─── The client ID this uses ─────────────────────────────────────────────────
 *
 * `GOOGLE_CLIENT_IDS.web` is the same Google Cloud OAuth web client Android
 * sign-in uses as `serverClientId` (see src/docs/GOOGLE_OAUTH_SETUP.md §8.2).
 * The `aud` of every id_token minted here is that client ID, which is already
 * listed in the Supabase Google provider's "Authorized Client IDs" (otherwise
 * Android sign-in would not work), so no extra Supabase configuration is
 * needed for this flow.
 *
 * The current browser origin must be listed under the web client's
 * "Authorized JavaScript origins" in Google Cloud Console. Production
 * (`https://gotradeloop.com`) already is because Supabase redirects to it.
 * Local dev origins (`http://localhost:3000`) need to be added there manually
 * for testing.
 */

import { GOOGLE_CLIENT_IDS } from '@/config/googleAuth';

/** URL of Google Identity Services library. Loaded lazily on first use. */
const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

/** The subset of GIS types we use. Kept local so we can drop the whole dep in one place. */
interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    /** SHA-256 of a random string; echoed into the id_token's `nonce` claim. */
    nonce?: string;
    /** Prevent auto-prompt on unrelated pages. Reauth is always a deliberate action. */
    auto_select?: boolean;
    /** Disable one-tap-cooldown so a rejected attempt can be retried immediately. */
    itp_support?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (
    container: HTMLElement,
    options: {
      type?: 'standard' | 'icon';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'large' | 'medium' | 'small';
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      logo_alignment?: 'left' | 'center';
      width?: number | string;
    }
  ) => void;
  cancel: () => void;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: GoogleAccountsId;
      };
    };
  }
}

/**
 * Insert the GIS script tag once and resolve when it is ready.
 *
 * Idempotent — a second call while the first is still resolving returns the
 * same promise, and a call after the script is loaded resolves immediately.
 * A failure clears the cached promise so a later retry can rebuild it (the
 * network was down, the user came back).
 */
let loadPromise: Promise<void> | null = null;

export function ensureGoogleIdentityServicesLoaded(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();

  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SCRIPT_SRC}"]`);
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.src = GIS_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const onLoad = () => {
      if (window.google?.accounts?.id) {
        resolve();
      } else {
        // Script tag "load" fired but the global didn't populate — treat as a
        // load failure so the caller can retry rather than hang.
        reject(new Error('Google Identity Services loaded but window.google.accounts.id is missing.'));
      }
    };
    const onError = () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Identity Services.'));
    };

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });

    // Script may already be cached and 'load' fired before our listener; poll
    // briefly as a safety net so a page-navigation-cached script does not hang.
    if (window.google?.accounts?.id) {
      resolve();
    }
  }).catch((error) => {
    loadPromise = null;
    throw error;
  });

  return loadPromise;
}

/** Helper: 32 random bytes as hex, and its SHA-256 hex, mirroring the native path. */
const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

async function generateNoncePair(): Promise<{ raw: string; hashed: string }> {
  const webCrypto = globalThis.crypto;
  if (!webCrypto?.subtle || !webCrypto.getRandomValues) {
    throw new Error('This browser cannot generate a secure sign-in nonce (WebCrypto unavailable).');
  }
  const raw = toHex(webCrypto.getRandomValues(new Uint8Array(32)));
  const digest = await webCrypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  return { raw, hashed: toHex(new Uint8Array(digest)) };
}

/**
 * Render a Sign in with Google button inside `container` and resolve the
 * callback when the user picks an account.
 *
 * We use `renderButton` (not `prompt()`) deliberately. `prompt()` shows the
 * One Tap UI, which:
 *  - is silently rate-limited if the user recently dismissed it,
 *  - assumes an ambient session (no explicit account picker),
 *  - is Chrome/Chromium-only in practice.
 * For a destructive action a rendered button is the right UX: the user
 * explicitly picks the account they want to prove ownership of.
 *
 * The `raw` nonce is returned alongside the id_token so the caller can POST
 * both to the backend, mirroring the native path's contract.
 */
export interface WebGoogleReauthController {
  /** Remove the rendered button and detach the GIS callback. */
  dispose: () => void;
}

export interface WebGoogleReauthCallbacks {
  onSuccess: (result: { idToken: string; nonce: string }) => void;
  onError?: (error: Error) => void;
}

export async function renderWebGoogleReauthButton(
  container: HTMLElement,
  callbacks: WebGoogleReauthCallbacks
): Promise<WebGoogleReauthController> {
  await ensureGoogleIdentityServicesLoaded();

  const gis = window.google?.accounts?.id;
  if (!gis) {
    throw new Error('Google Identity Services is unavailable.');
  }

  let disposed = false;
  const { raw, hashed } = await generateNoncePair();

  gis.initialize({
    client_id: GOOGLE_CLIENT_IDS.web,
    // Nonce protects against id_token replay: even a valid token cannot be
    // reused for a different reauth attempt because the backend hands each
    // id_token straight to Supabase's signInWithIdToken, which requires the
    // raw nonce (we pass it through as `nonce`) to match the SHA-256 that
    // went to Google here.
    nonce: hashed,
    auto_select: false,
    cancel_on_tap_outside: false,
    callback: (response) => {
      if (disposed) return;
      if (!response?.credential) {
        callbacks.onError?.(new Error('Google did not return an identity token.'));
        return;
      }
      callbacks.onSuccess({ idToken: response.credential, nonce: raw });
    },
  });

  // Clear anything we (or a previous mount of this component) rendered — GIS
  // does not clean up its own DOM if renderButton is called twice on the same
  // container, and the leftover node would receive stale clicks.
  container.innerHTML = '';

  gis.renderButton(container, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    shape: 'rectangular',
    logo_alignment: 'left',
    // Full-width fits the confirmation card better than the default 200px.
    width: Math.min(container.clientWidth || 320, 400),
  });

  return {
    dispose: () => {
      disposed = true;
      try {
        gis.cancel();
      } catch {
        // cancel() throws if no prompt was ever shown — harmless.
      }
      container.innerHTML = '';
    },
  };
}
